import React, { useState } from 'react';
import { History, AlertTriangle, Check, GitCompare } from 'lucide-react';
import type { DealVersionChange } from '@platform/shared-types';
import { dealsApi, type DealVersionRow } from '../../services/dealsApi';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * THE VERSION TIMELINE for one deal.
 *
 * A deal is the billing contract, so editing a live one rewrites what
 * already-signed merchants agreed to. This panel is where that stops being
 * invisible: every published version, what changed between any two of them,
 * which pinned documents have drifted, and the Publish that freezes the next
 * one.
 *
 * A DEAL WITH NO VERSIONS IS NORMAL. Every deal that existed before versioning
 * is its own implicit v1 — the panel says so plainly rather than showing an
 * empty error state, and Publish is how it gains a real v1.
 */
export interface DealVersionsPanelProps {
  dealId: string;
  /**
   * THE DATA COMES FROM THE PAGE. The banner, the tab badge and this view must
   * never disagree about what is published, so there is one fetch and three
   * readers rather than three fetches that drift.
   */
  versions: any[];
  activeVersionNo: number | null;
  implicit: boolean;
  loading?: boolean;
  error?: string | null;
  onReload?: () => void;
  /**
   * The deal AS IT STANDS IN THE FORM — used to say plainly when the live deal
   * has drifted from the live version. An operator who edits a price and saves
   * must not be left believing it reached merchants who already signed: it did
   * not, and will not until a version is published.
   */
  liveDeal?: { priceCents?: number | null; packageIds?: string[] };
  /** A one-line result from an action taken in the top bar (draft saved, …). */
  note?: string | null;
}

const STATUS_CHIP: Record<string, string> = {
  published: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  draft: 'bg-stone-100 text-stone-600 border-stone-200',
  retired: 'bg-amber-100 text-amber-800 border-amber-200',
};

const when = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

export const DealVersionsPanel: React.FC<DealVersionsPanelProps> = ({
  dealId, versions, activeVersionNo, implicit, loading, error, onReload,
  note, liveDeal,
}) => {
  const [compareError, setCompareError] = useState<string | null>(null);

  const [compare, setCompare] = useState<{ from: number; to: number; changes: DealVersionChange[] } | null>(null);
  const [impactFilter, setImpactFilter] = useState<'' | DealVersionChange['impact']>('');



  /**
   * HOW MANY SIGNED PARTIES ARE ON AN OLDER VERSION. Derived from what the
   * panel already knows: everyone not on the newest published version. `null`
   * until the list has loaded — the modal refuses to publish on an unknown
   * radius rather than showing a confident zero.
   */
  const affectedCount = loading || error ? null
    : versions.filter((v) => v.status === 'published' && v.versionNo !== activeVersionNo).length;

  const doCompare = async (to: number) => {
    const older = versions.filter((v) => v.versionNo < to).sort((a, b) => b.versionNo - a.versionNo)[0];
    if (!older) return;
    try {
      setCompare(await dealsApi.diff(dealId, to, older.versionNo));
    } catch (e: any) {
      setCompareError(e?.message || 'Could not compare those versions');
    }
  };

  /**
   * HAS THE LIVE DEAL MOVED AWAY FROM THE LIVE VERSION?
   *
   * Saving a deal changes what NEW signups get quoted. It does not change what
   * anyone already pinned is billed or signed against — only publishing does.
   * Those are two different facts and the gap between them is exactly where an
   * operator would otherwise assume the wrong one.
   */
  const live = versions.find((v) => v.versionNo === activeVersionNo) as any;
  const drift: string[] = [];
  if (live && liveDeal) {
    if (typeof liveDeal.priceCents === 'number' && typeof live.priceCents === 'number'
      && liveDeal.priceCents !== live.priceCents) {
      drift.push(`price (v${live.versionNo} sells at $${(live.priceCents / 100).toFixed(2)}, `
        + `your unpublished draft is $${(liveDeal.priceCents / 100).toFixed(2)})`);
    }
    const a = [...(live.packageIds || [])].sort().join(',');
    const b = [...(liveDeal.packageIds || [])].sort().join(',');
    if (a !== b) drift.push('packages');
  }

  const shownChanges = compare?.changes.filter((c) => !impactFilter || c.impact === impactFilter) || [];

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <History size={15} className="text-stone-500" />
          <h3 className="text-sm font-semibold text-stone-800">Versions</h3>
          <span className="text-[11px] text-stone-400">
            what merchants signed against, frozen
          </span>
        </div>
        {/* NO PUBLISH BUTTON HERE. Publishing is one action and it lives in
            the deal's top bar beside Save — two buttons that both publish is
            two places to look and two states to keep in step. */}
        {onReload && (
          <button
            onClick={onReload}
            className="rounded-lg border border-stone-300 px-3 py-1.5 text-[11.5px] font-semibold text-stone-600 hover:bg-stone-50"
          >
            Refresh
          </button>
        )}
      </div>

      {note && (
        <p className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 text-[11.5px] text-emerald-900">
          {note}
        </p>
      )}

      {/*
        UNPUBLISHED WORK, NOT "the live deal has moved on".

        This banner used to read the DEAL ROW as "the deal now" and tell the
        operator that new signups were being quoted it. Both halves were wrong,
        and together they were alarming: it fired on every published deal whose
        price had ever been edited, for ever.

        The row does not track commercial fields at all — `updateDeal` routes
        them to the draft and never writes them there — so it holds whatever the
        deal was CREATED with, and comparing a version against it always
        "drifts". And a new signup is not quoted the row: `tenants-service`
        pins the tenant to the ACTIVE published version at signup
        (`dealVersionNo: args.deal.activeVersionNo`), so what a new customer
        gets is v{activeVersionNo}, not the row.

        What is worth saying is the one thing that is true and actionable: there
        is saved work nobody has published, and until somebody does it reaches
        no one at all.
      */}
      {drift.length > 0 && (
        <p className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] text-amber-900">
          <b>You have unpublished changes:</b> {drift.join(', ')}. Nobody is being sold this yet —
          new signups are pinned to v{activeVersionNo} and everybody already on it keeps what they
          signed. Publish to make these the terms customers get.
        </p>
      )}

      {compareError && (
        <p className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] text-amber-900">{compareError}</p>
      )}

      {loading ? (
        <RubyLoader variant="section" />
      ) : error ? (
        <p className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] text-amber-900">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          {error}
        </p>
      ) : implicit ? (
        <p className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-[11.5px] text-stone-600">
          This deal has never published a version, so the deal as it stands is its <b>implicit v1</b> — every
          merchant on it signed against whatever it said at the time. Publishing freezes what it says now, and
          from then on a signed merchant keeps the version they signed.
        </p>
      ) : (
        <div className="space-y-1.5">
          {versions.map((v) => {
            const isActive = v.versionNo === activeVersionNo;
            return (
              <div
                key={v.versionNo}
                className={`flex flex-wrap items-center gap-2 rounded-lg border p-2.5 ${
                  isActive ? 'border-emerald-300 bg-emerald-50/50' : 'border-stone-200'
                }`}
              >
                <span className="font-mono text-[13px] font-bold text-stone-900">v{v.versionNo}</span>
                <span className={`rounded border px-1.5 py-0.5 text-[9.5px] font-bold uppercase ${STATUS_CHIP[v.status] || STATUS_CHIP.draft}`}>
                  {v.status}
                </span>
                {isActive && (
                  <span className="flex items-center gap-1 text-[10.5px] font-semibold text-emerald-700">
                    <Check size={11} /> live
                  </span>
                )}
                {v.changeType && (
                  <span className={`text-[10.5px] font-semibold ${v.changeType === 'material' ? 'text-rose-700' : 'text-stone-500'}`}>
                    {v.changeType}
                  </span>
                )}
                <span className="ml-auto text-[11px] text-stone-500">
                  {when(v.effectiveAt)}{v.publishedBy ? ` · ${v.publishedBy}` : ''}
                </span>
                {versions.some((o) => o.versionNo < v.versionNo) && (
                  <button
                    onClick={() => doCompare(v.versionNo)}
                    title="Compare with the version before it"
                    className="flex items-center gap-1 rounded-md border border-stone-300 px-2 py-1 text-[10.5px] font-semibold text-stone-600 hover:bg-stone-50"
                  >
                    <GitCompare size={11} /> Compare
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {compare && (
        <div className="mt-3 rounded-xl border border-stone-200 bg-stone-50/70 p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">
              v{compare.from} → v{compare.to}
            </span>
            <div className="flex gap-1.5">
              {(['', 'money', 'legal', 'presentation'] as const).map((f) => (
                <button
                  key={f || 'all'}
                  onClick={() => setImpactFilter(f)}
                  className={`rounded-full border px-2.5 py-1 text-[10.5px] font-semibold ${
                    impactFilter === f
                      ? 'border-emerald-600 bg-emerald-600 text-white'
                      : 'border-stone-300 bg-white text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  {f || 'All'}
                </button>
              ))}
              <button
                onClick={() => setCompare(null)}
                className="rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[10.5px] font-semibold text-stone-500"
              >
                Close
              </button>
            </div>
          </div>
          {shownChanges.length === 0 ? (
            <p className="text-[11.5px] text-stone-500">Nothing in that category changed.</p>
          ) : (
            <div className="space-y-1">
              {shownChanges.map((c) => (
                <div key={c.path} className="flex flex-wrap items-baseline gap-2 rounded-lg bg-white px-2.5 py-1.5">
                  <span className="text-[12px] font-semibold text-stone-800">{c.label}</span>
                  <span className="font-mono text-[11px] text-stone-500">{c.before ?? '—'} → {c.after ?? '—'}</span>
                  <span className="ml-auto text-[10px] uppercase text-stone-400">{c.impact}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default DealVersionsPanel;
